import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'react-hot-toast';
import { base44, supabase } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientSession } from '@/lib/ClientSessionContext';
import { logAuditEvent } from '@/utils/AuditLogManager';

const setCookie = (name, value, days = 365) => {
  try {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;expires=${d.toUTCString()};SameSite=Lax`;
  } catch (e) {}
};

const getCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) { return null; }
};

const deleteCookie = (name) => {
  try {
    document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch (e) {}
};

export default function ClientLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { loginClient } = useClientSession();
  const [isLogin, setIsLogin] = useState(true); // true = Masuk, false = Daftar
  const [showPassword, setShowPassword] = useState(false);
  
  // Login Form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginStatus, setLoginStatus] = useState('idle'); // 'idle' | 'not_found' | 'wrong_password'

  // Reset Password Flow States
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1 = Email, 2 = OTP, 3 = New Password
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetClientRecord, setResetClientRecord] = useState(null);

  // Check remembered email on mount via Cookie (365 days)
  useEffect(() => {
    deleteCookie('satala_client_remembered_pass'); // Cleanup legacy pass cookie if any
    const savedEmail = getCookie('satala_client_remembered_email') || localStorage.getItem('satala_client_remembered_email');
    if (savedEmail) {
      setLoginEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Register Form
  const [regName, setRegName] = useState('');
  const [regBrand, setRegBrand] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');

  // Fetch all ClientAccounts to mock validation
  const { data: clients = [] } = useQuery({
    queryKey: ["client-accounts"],
    queryFn: async () => {
      try {
        return await base44.entities.ClientAccount.list();
      } catch (e) {
        return [];
      }
    }
  });

  const createClientMutation = useMutation({
    mutationFn: async (data) => {
      return base44.entities.ClientAccount.create({
        ...data,
        status: 'unverified',
        database_code: '',
        credit_limit: 0,
        created_date: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
    }
  });

  const doClientLoginSession = async (clientData) => {
    await loginClient(clientData);
    logAuditEvent({
      actor: clientData.contact_person || clientData.brand_name || 'Satala Dev Client',
      role: 'Client',
      action: 'Login Akun Klien',
      target: 'Portal Client (/profil-saya)',
      ip: '127.0.0.1 (Localhost)',
      browser: 'Chrome 122',
      device: 'Desktop PC (Windows)',
      country: 'Indonesia',
      result: 'Sukses',
      severity: 'INFO',
      details: `Client ${clientData.brand_name || clientData.contact_person} logged in successfully.`
    });
    toast.success(`Selamat datang, ${clientData.brand_name || clientData.contact_person || 'Client'}!`);
    const from = new URLSearchParams(location.search).get('redirect') || '/';
    navigate(from === '/dashboard-client' ? '/' : from);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Harap isi Email/Telepon dan Kata Sandi');
      return;
    }

    toast.loading('Sedang masuk...', { id: 'login' });
    try {
      if (rememberMe) {
        setCookie('satala_client_remembered_email', loginEmail, 365);
        localStorage.setItem('satala_client_remembered_email', loginEmail);
      } else {
        deleteCookie('satala_client_remembered_email');
        localStorage.removeItem('satala_client_remembered_email');
      }
      deleteCookie('satala_client_remembered_pass');

      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) {
        setLoginStatus('wrong_password');
        throw error;
      }

      // Cari data klien di database
      const { data: clientsData } = await supabase.from('ClientAccount').select('*');
      const matchedClient = clientsData?.find(row => {
        const item = { ...row.data, id: row.id };
        return (item.email || '').trim().toLowerCase() === loginEmail.trim().toLowerCase();
      });

      if (matchedClient) {
        const isDeleted = Boolean(matchedClient.data?.deleted_at) || 
                          ['inactive', 'nonaktif', 'deleted', 'blocked', 'suspended'].includes((matchedClient.data?.status || '').toLowerCase());
        
        if (isDeleted) {
          await supabase.auth.signOut();
          toast.error('Akun Anda telah dinonaktifkan atau dihapus dari sistem. Silakan hubungi admin.', { id: 'login' });
          return;
        }

        const clientData = { 
          ...matchedClient.data, 
          id: matchedClient.id, 
          created_date: matchedClient.created_date, 
          updated_date: matchedClient.updated_date 
        };
        toast.success('Berhasil masuk!', { id: 'login' });
        doClientLoginSession(clientData);
      } else {
        await supabase.auth.signOut();
        toast.error('Akun belum terdaftar lengkap atau tidak ditemukan di sistem. Silakan hubungi admin.', { id: 'login' });
      }
    } catch (e) {
      toast.error(e.message === 'Invalid login credentials' ? 'Email atau kata sandi salah!' : e.message, { id: 'login' });
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (regPassword !== regConfirm) {
      toast.error('Konfirmasi kata sandi tidak cocok!');
      return;
    }

    toast.loading('Memproses pendaftaran...', { id: 'reg' });
    try {
      // 1. Daftar ke Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: regEmail,
        password: regPassword,
        options: {
          data: {
            brand_name: regBrand,
            phone: regPhone
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          toast.error('Email sudah terdaftar! Silakan masuk.', { id: 'reg' });
          setIsLogin(true);
          setLoginEmail(regEmail);
          return;
        }
        throw authError;
      }

      // 2. Buat profil di ClientAccount
      const newClient = await createClientMutation.mutateAsync({
        brand_name: regBrand,
        company_name: regBrand + ' (Baru)',
        email: regEmail,
        pic: 'Admin ' + regBrand,
        phone: regPhone,
        password: '' // Kosongkan karena pakai Supabase Auth
      });
      
      toast.success('Pendaftaran Berhasil!', { id: 'reg' });
      doClientLoginSession(newClient);
    } catch (e) {
      console.error('Pendaftaran gagal:', e);
      toast.error('Gagal mendaftarkan akun: ' + e.message, { id: 'reg' });
    }
  };

  // Google OAuth Listener & Auto-Registration
  // Google OAuth Listener & Auto-Registration
  useEffect(() => {
    const checkOAuthSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
          const email = session.user.email;
          toast.loading('Sinkronisasi akun Google...', { id: 'google-sync' });

          // 1. Fetch clients from database using Supabase JS client
          const { data: clientsData, error } = await supabase
            .from('ClientAccount')
            .select('*');
          
          if (error) throw error;
          
          const matched = clientsData?.find(row => {
            const item = { ...row.data, id: row.id };
            return (item.email || '').trim().toLowerCase() === email.trim().toLowerCase();
          });

          if (matched) {
            const clientData = { 
              ...matched.data, 
              id: matched.id, 
              created_date: matched.created_date, 
              updated_date: matched.updated_date 
            };
            if (clientData.phone === '' || clientData.brand_name === 'Mitra Baru') {
              // Redirect to Onboarding
              toast.success('Koneksi Google Sukses!', { id: 'google-sync' });
              await loginClient(clientData);
              navigate('/client-onboarding');
            } else {
              toast.success('Koneksi Google Sukses!', { id: 'google-sync' });
              doClientLoginSession(clientData);
            }
          } else {
            // Jika belum terdaftar, otomatis daftarkan sebagai Mitra Baru!
            toast.loading('Mendaftarkan akun Google Anda sebagai Mitra...', { id: 'google-sync' });
            const newId = 'cli_' + Date.now();
            const newClientData = {
              brand_name: session.user.user_metadata?.full_name || 'Mitra Baru',
              company_name: session.user.user_metadata?.full_name || 'Mitra Baru',
              email: email,
              pic: session.user.user_metadata?.full_name || 'Mitra',
              phone: '',
              status: 'unverified',
              password: '', // OAuth users don't need passwords
              created_date: new Date().toISOString()
            };
            
            const { error: regError } = await supabase.from('ClientAccount').insert({
              id: newId,
              created_date: new Date().toISOString(),
              updated_date: new Date().toISOString(),
              data: newClientData
            });

            if (regError) throw regError;

            toast.success('Pendaftaran Google Berhasil!', { id: 'google-sync' });
            await loginClient({ ...newClientData, id: newId });
            navigate('/client-onboarding');
          }
        }
      } catch (err) {
        console.error('OAuth session check failed:', err);
        toast.error('Gagal memproses login Google: ' + err.message, { id: 'google-sync' });
      }
    };

    checkOAuthSession();
  }, []);

  const handleSendResetOtp = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Harap masukkan email Anda');
      return;
    }
    
    toast.loading('Mengirim tautan reset...', { id: 'reset' });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin + '/login?tab=reset',
      });
      if (error) throw error;
      
      toast.success('Tautan reset kata sandi telah dikirim ke email Anda!', { id: 'reset' });
      setShowResetModal(false);
    } catch (err) {
      toast.error('Gagal mengirim reset: ' + err.message, { id: 'reset' });
    }
  };

  const handleVerifyResetOtp = (e) => {
    e.preventDefault();
    // Di Supabase, OTP bisa dimasukkan lewat signInWithOtp atau user bisa langsung klik link.
    // Untuk mempermudah, kita asumsikan user klik link dari email yang akan me-redirect ke mode ubah sandi.
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    // Jika flow menggunakan input password baru setelah klik link:
    if (newPassword !== confirmNewPassword) {
      toast.error('Konfirmasi kata sandi tidak cocok!');
      return;
    }

    toast.loading('Memperbarui kata sandi...', { id: 'update-pass' });
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      toast.success('Kata sandi berhasil diperbarui! Silakan masuk.', { id: 'update-pass' });
      
      // Reset state & close modal
      setShowResetModal(false);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      toast.error('Gagal memperbarui kata sandi: ' + err.message, { id: 'update-pass' });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/login'
        }
      });
      if (error) throw error;
    } catch (err) {
      toast.error('Gagal masuk dengan Google: ' + err.message);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      
      {/* KIRI - HERO SECTION */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900 overflow-hidden text-white flex-col justify-end">
        {/* Background Image / Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1570823635306-250abb06d4b3?q=80&w=2070&auto=format&fit=crop" 
            alt="Cosmetic Production"
            className="w-full h-full object-cover opacity-50 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
        </div>

        {/* Brand & Content */}
        <div className="relative z-10 p-16">
          <div className="mb-12">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="Satala Dermatech Essential" className="h-12 w-auto object-contain drop-shadow-lg" />
              <h1 className="text-3xl font-black tracking-tight text-white drop-shadow-sm">
                Satala <span className="text-[#D4AF37]">Dermatech</span> Essential
              </h1>
            </div>
          </div>

          <div className="bg-white/10  border border-white/20 p-6 rounded-3xl mb-8 max-w-lg shadow-2xl relative">
            <div className="absolute -top-6 -left-6 bg-white w-12 h-12 rounded-full flex items-center justify-center text-3xl shadow-lg border-4 border-slate-900">
              <span className="text-[#302621]">❝</span>
            </div>
            <p className="text-xl font-bold leading-relaxed mb-4">
              Buat produk perawatan kulitmu lebih eksklusif! Formulasi premium, perizinan legalitas aman, dan dukungan penuh Satala.
            </p>
            <p className="text-[#D4AF37] font-black tracking-wider text-sm">#MulaiBrandMu</p>
          </div>

          <div className="flex gap-2">
            <div className="w-10 h-1.5 bg-[#D4AF37] rounded-full"></div>
            <div className="w-4 h-1.5 bg-white/30 rounded-full"></div>
            <div className="w-4 h-1.5 bg-white/30 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* KANAN - FORM SECTION */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 lg:p-20 relative bg-white">
        
        {/* Tombol Kembali (Mobile) */}
        <button onClick={() => navigate('/')} className="absolute top-6 left-6 lg:hidden flex items-center gap-2 text-xs font-bold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
          Kembali ke Beranda
        </button>

        <div className="w-full max-w-md space-y-8 mt-10 lg:mt-0">
          
          <div className="text-center space-y-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-6">
              <div className="w-8 h-8 bg-[#8C6208] rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-xl font-black text-[#302621]">Satala Dermatech</h1>
            </div>
            <h2 className="text-3xl font-black text-[#302621]">{isLogin ? 'Masuk' : 'Daftar'}</h2>
            <p className="text-sm text-slate-500">Pusat kontrol pemesanan dan inventori brand kosmetik Anda secara real-time.</p>
          </div>

          {/* Toggle Tab */}
          <div className="flex border-b border-slate-200 justify-center">
            <button 
              onClick={() => setIsLogin(true)}
              className={`pb-3 px-6 text-sm font-black transition-colors border-b-2 ${isLogin ? 'border-[#8C6208] text-[#302621]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            >
              Masuk
            </button>
            <button 
              onClick={() => setIsLogin(false)}
              className={`pb-3 px-6 text-sm font-black transition-colors border-b-2 ${!isLogin ? 'border-[#8C6208] text-[#302621]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            >
              Daftar
            </button>
          </div>

          <div className="mt-8">
            {isLogin ? (
              // FORM LOGIN
              <form onSubmit={handleLoginSubmit} className="space-y-5 animate-in fade-in zoom-in-95 duration-300">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 block">Nomor Telepon atau Email</label>
                  <Input 
                    value={loginEmail}
                    onChange={e => { setLoginEmail(e.target.value.toLowerCase()); setLoginStatus('idle'); }}
                    placeholder="Masukkan Email atau Nomor HP terdaftar"
                    autoComplete="username email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    className="h-12 border-slate-300 focus-visible:ring-[#8C6208] focus-visible:border-[#8C6208] bg-slate-50 focus:bg-white"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600 block">Kata Sandi</label>
                  <div className="relative">
                    <Input 
                      type={showPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={e => { setLoginPassword(e.target.value); setLoginStatus('idle'); }}
                      placeholder="Masukkan kata sandi akun"
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      className="h-12 border-slate-300 pr-10 focus-visible:ring-[#8C6208] focus-visible:border-[#8C6208] bg-slate-50 focus:bg-white"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3.5 text-slate-400 hover:text-[#8C6208]">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                 <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-600">
                    <input 
                      type="checkbox" 
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      className="rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]" 
                    />
                    Ingat Saya
                  </label>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowResetModal(true);
                      setResetStep(1);
                    }}
                    className="text-xs font-bold text-[#8C6208] hover:underline"
                  >
                    Lupa Kata Sandi?
                  </button>
                </div>

                <button 
                  type="submit" 
                  className={`w-full font-black h-12 rounded-xl transition-all shadow-sm ${
                    loginStatus === 'not_found' || loginStatus === 'wrong_password'
                      ? 'bg-red-600 hover:bg-red-700 text-white shadow-md'
                      : (loginEmail.trim().length > 0 && loginPassword.length > 0)
                        ? 'bg-[#302621] hover:bg-[#1A1411] text-white shadow-md'
                        : 'bg-[#EAEAEA] hover:bg-[#d5d5d5] text-[#302621] hover:text-[#1A1411]'
                  }`}
                >
                  {loginStatus === 'not_found' ? 'Akun belum terdaftar, silahkan daftar terlebih dahulu' : loginStatus === 'wrong_password' ? 'Kata sandi salah!' : 'Masuk'}
                </button>
              </form>
            ) : (
              // FORM DAFTAR
              <form onSubmit={handleRegisterSubmit} className="space-y-4 animate-in fade-in zoom-in-95 duration-300">
                
                <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-xl flex gap-2 text-xs">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>Jika sebelumnya mendaftar via tim sales kami, masukkan email yang didaftarkan. Sistem akan menyinkronkan profil Anda.</p>
                </div>

                <div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">Nama Brand Utama</label>
                    <Input value={regBrand} onChange={e => setRegBrand(e.target.value)} placeholder="Contoh: Cielmora" className="h-10 text-xs border-slate-300 focus-visible:ring-[#8C6208]" required />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">Email</label>
                    <Input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="email@brand.com" className="h-10 text-xs border-slate-300 focus-visible:ring-[#8C6208]" required />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">No. Telepon / WA</label>
                    <Input value={regPhone} onChange={e => setRegPhone(e.target.value)} placeholder="0856xxxxxx" className="h-10 text-xs border-slate-300 focus-visible:ring-[#8C6208]" required />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">Sandi Baru</label>
                    <Input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Minimal 6 karakter" className="h-10 text-xs border-slate-300 focus-visible:ring-[#8C6208]" required minLength={6} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">Ulangi Sandi</label>
                    <Input type="password" value={regConfirm} onChange={e => setRegConfirm(e.target.value)} placeholder="Konfirmasi sandi" className="h-10 text-xs border-slate-300 focus-visible:ring-[#8C6208]" required />
                  </div>
                </div>

                <button type="submit" className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-black h-12 rounded-xl transition-all shadow-md mt-2 flex justify-center items-center gap-2">
                  Daftar Akun Mitra <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}

            {/* OAUTH MOCK */}
            <div className="mt-8 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-slate-400">Atau masuk dengan</span>
              </div>
            </div>

            <button 
              onClick={handleGoogleLogin}
              className="mt-6 w-full h-12 flex items-center justify-center gap-3 bg-white border border-slate-300 hover:bg-slate-50 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Masuk dengan Google
            </button>

            {/* MODAL RESET PASSWORD PREMIUM */}
            {showResetModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs animate-in fade-in duration-200">
                <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl relative border border-slate-100 text-left">
                  <button 
                    onClick={() => setShowResetModal(false)}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 text-lg font-bold"
                  >
                    ✕
                  </button>

                  <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] p-0.5 mx-auto mb-3 shadow-md bg-gradient-to-tr from-[#8C6208] to-[#D4AF37] overflow-hidden">
                    <img 
                      src="/asa-profile.png" 
                      alt="Asa AI Assistant" 
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  
                  <h3 className="text-xl font-black text-[#302621] text-center mb-1">Atur Ulang Kata Sandi</h3>
                  <p className="text-xs text-slate-400 text-center mb-6 font-semibold">Bantuan Keamanan Akun Asa Satala</p>

                  {resetStep === 1 && (
                    <form onSubmit={handleSendResetOtp} className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-600 block">Email Mitra</label>
                        <Input 
                          type="email"
                          value={resetEmail}
                          onChange={e => setResetEmail(e.target.value)}
                          placeholder="Masukkan email terdaftar"
                          className="h-12 border-slate-300 focus-visible:ring-[#8C6208]"
                          required
                        />
                      </div>
                      <button type="submit" className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-black h-12 rounded-xl transition-all shadow-md">
                        Kirim Kode Verifikasi
                      </button>
                    </form>
                  )}

                  {resetStep === 2 && (
                    <form onSubmit={handleVerifyResetOtp} className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-600 block">Kode OTP Verifikasi</label>
                        <Input 
                          value={resetOtp}
                          onChange={e => setResetOtp(e.target.value)}
                          placeholder="Masukkan 6 digit OTP (Simulasi: 123456)"
                          className="h-12 border-slate-300 text-center font-mono tracking-widest text-lg focus-visible:ring-[#8C6208]"
                          maxLength={6}
                          required
                        />
                        <p className="text-[10px] text-slate-500 text-center mt-2">Kode OTP simulasi telah dikirim ke <b>{resetEmail}</b></p>
                      </div>
                      <button type="submit" className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-black h-12 rounded-xl transition-all shadow-md">
                        Verifikasi OTP
                      </button>
                    </form>
                  )}

                  {resetStep === 3 && (
                    <form onSubmit={handleUpdatePassword} className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-600 block">Kata Sandi Baru</label>
                        <Input 
                          type="password"
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          placeholder="Minimal 6 karakter"
                          className="h-12 border-slate-300 focus-visible:ring-[#8C6208]"
                          minLength={6}
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-600 block">Konfirmasi Kata Sandi Baru</label>
                        <Input 
                          type="password"
                          value={confirmNewPassword}
                          onChange={e => setConfirmNewPassword(e.target.value)}
                          placeholder="Ulangi kata sandi baru"
                          className="h-12 border-slate-300 focus-visible:ring-[#8C6208]"
                          required
                        />
                      </div>
                      <button type="submit" className="w-full bg-[#8C6208] hover:bg-[#6b4a04] text-white font-black h-12 rounded-xl transition-all shadow-md">
                        Simpan & Atur Ulang Sandi
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
